import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleHelp, ListPlus, UserRoundCog } from 'lucide-react';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import type { TaskListScope, TaskRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneChip, toneCardClass, type Tone } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { errorMessage } from '../../../lib/api-client';
import { formatDate, formatDateTimeLong, formatNumber, formatRelative } from '../../../lib/format';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { TASKS_KEY, TIMELINE_KEY, communicationApi, useMyTasks, useTaskAssignees } from '../api/communication-api';
import styles from '../Communication.module.css';

type Panel = { readonly kind: 'answer' | 'reassign'; readonly task: TaskRow; readonly anchor: AnchorRect } | null;

/**
 * COMM-04 — "everything with a next-action due date; overdue first. This is the recruiter's
 * morning screen" (E13). Ticking a task done happens at once, with ten seconds to undo; an
 * "ask HR" question is answered as it is ticked, and the hiring manager receives the answer.
 */
export function MyTasksPage({ focusTaskId }: { readonly focusTaskId?: string }): ReactNode {
  const canUse = useCanEdit('activity_message');
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const [scope, setScope] = useState<TaskListScope>('open');
  const tasks = useMyTasks(scope);
  const [pendingDone, setPendingDone] = useState<ReadonlySet<string>>(new Set());
  const [panel, setPanel] = useState<Panel>(null);
  const { anchor: addAnchor, triggerRef: addTrigger, toggle: toggleAdd, close: closeAdd } = usePopover();
  const focusRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    focusRef.current?.scrollIntoView({ block: 'center' });
  }, [tasks.data]);

  if (!canUse) return <ForbiddenState resource="tasks" contact="Your admin" />;

  const refresh = async (): Promise<void> => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: TASKS_KEY }), queryClient.invalidateQueries({ queryKey: TIMELINE_KEY })]);
  };

  /** Act first (CLAUDE.md §5a): the row reads done at once; the server is told after. */
  const complete = async (task: TaskRow, answer: string | null): Promise<void> => {
    setPendingDone((current) => new Set(current).add(task.id));
    try {
      const done = await communicationApi.completeTask(task.id, task.version, answer);
      await refresh();
      if (task.kind === 'ask_hr') show({ tone: 'success', message: 'Answer sent to the hiring manager' });
      else
        showUndo(`Done: ${task.title}`, () => {
          void communicationApi
            .reopenTask(task.id, done.version)
            .then(refresh)
            .catch((cause: unknown) => show({ tone: 'danger', message: "It couldn't be reopened", detail: errorMessage(cause) }));
        });
    } catch (cause) {
      show({ tone: 'danger', message: "The task wasn't marked done", detail: errorMessage(cause) });
    } finally {
      setPendingDone((current) => {
        const next = new Set(current);
        next.delete(task.id);
        return next;
      });
    }
  };

  const view = tasks.data;
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>My tasks</h1>
          <p className={styles.lead}>Follow-ups from calls and notes, questions from hiring managers, and anything you add. Overdue first.</p>
        </div>
        <div className={styles.actions}>
          <Segmented
            label="Show"
            value={scope}
            options={[
              { value: 'open', label: 'To do' },
              { value: 'done', label: 'Done' },
            ]}
            onChange={setScope}
          />
          <Button ref={addTrigger} variant="solid" icon={ListPlus} onClick={toggleAdd}>
            Add task
          </Button>
        </div>
      </header>

      {scope === 'open' && view && (
        <div className={styles.counts}>
          <Count label="Overdue" value={view.counts.overdue} isDanger={view.counts.overdue > 0} />
          <Count label="Due today" value={view.counts.today} />
          <Count label="Later" value={view.counts.later} />
          <Count label="No date" value={view.counts.noDate} />
        </div>
      )}

      <Card isFlush>
        {tasks.error && !view ? (
          <ServerErrorState onRetry={() => void tasks.refetch()} />
        ) : !view ? (
          <SkeletonLines count={6} />
        ) : view.tasks.length === 0 ? (
          <EmptyState
            isInset
            title={scope === 'open' ? 'Nothing to do' : 'Nothing done yet'}
            body={scope === 'open' ? 'Log a call with a next action on any candidate, and the follow-up lands here.' : 'Tasks you finish appear here.'}
            action={null}
          />
        ) : (
          <ul className={styles.tasks}>
            {view.tasks.map((task) => (
              <li key={task.id} ref={task.id === focusTaskId ? focusRef : undefined} className={`${styles.task} ${task.id === focusTaskId ? styles.taskFocused : ''}`}>
                <Checkbox
                  checked={task.status === 'done' || pendingDone.has(task.id)}
                  disabled={task.status === 'done' || pendingDone.has(task.id)}
                  ariaLabel={`Done: ${task.title}`}
                  onChange={(_checked, event) => {
                    if (task.kind === 'ask_hr') setPanel({ kind: 'answer', task, anchor: anchorFrom(event.currentTarget.closest('li') ?? event.currentTarget) });
                    else void complete(task, null);
                  }}
                />
                <TaskDetails task={task} isDone={task.status === 'done' || pendingDone.has(task.id)} />
                {task.status !== 'done' && (
                  <span className={styles.actions}>
                    {task.kind === 'ask_hr' && (
                      <Button variant="quiet" size="sm" icon={CircleHelp} onClick={(event: MouseEvent<HTMLButtonElement>) => setPanel({ kind: 'answer', task, anchor: anchorFrom(event.currentTarget) })}>
                        Answer
                      </Button>
                    )}
                    <Button variant="quiet" size="sm" icon={UserRoundCog} onClick={(event: MouseEvent<HTMLButtonElement>) => setPanel({ kind: 'reassign', task, anchor: anchorFrom(event.currentTarget) })}>
                      Hand over
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Popover anchor={addAnchor} onClose={closeAdd} align="right" width={380} label="Add task">
        <AddTaskForm onDone={closeAdd} onCancel={closeAdd} />
      </Popover>
      <Popover anchor={panel?.anchor ?? null} onClose={() => setPanel(null)} align="right" width={400} label={panel?.kind === 'answer' ? 'Answer the question' : 'Hand over'}>
        {panel?.kind === 'answer' && (
          <AnswerForm
            task={panel.task}
            onCancel={() => setPanel(null)}
            onAnswer={(answer) => {
              const task = panel.task;
              setPanel(null);
              void complete(task, answer);
            }}
          />
        )}
        {panel?.kind === 'reassign' && <ReassignForm task={panel.task} onDone={() => setPanel(null)} onCancel={() => setPanel(null)} />}
      </Popover>
    </div>
  );
}

function Count({ label, value, isDanger = false }: { readonly label: string; readonly value: number; readonly isDanger?: boolean }): ReactNode {
  const area = useToneArea();
  const tone: Tone = isDanger ? 'urgent' : area.tone;
  return (
    <div className={`${styles.count} ${toneCardClass()}`} data-tone={tone}>
      <span className={styles.meta}>{label}</span>
      <span className={`${styles.countValue} ${isDanger ? styles.countDanger : ''}`}>{formatNumber(value)}</span>
    </div>
  );
}

function TaskDetails({ task, isDone }: { readonly task: TaskRow; readonly isDone: boolean }): ReactNode {
  return (
    <div className={styles.itemBody}>
      <span className={`${styles.taskTitle} ${isDone ? styles.taskDone : ''}`}>{task.title}</span>
      <span className={styles.meta}>
        {task.candidate && (
          <>
            <Link to={task.applicationId ? '/applications/$applicationId' : '/candidates/$candidateId'} params={task.applicationId ? { applicationId: task.applicationId } : { candidateId: task.candidate.id }} className={styles.name}>
              {task.candidate.fullName}
            </Link>
            {task.jobTitle ? ` · ${task.jobTitle}` : ''}
            {' · '}
          </>
        )}
        {task.requisition && (
          <>
            {task.requisition.title} ({task.requisition.reference}){' · '}
          </>
        )}
        {task.dueAt ? <span title={formatDateTimeLong(task.dueAt)}>Due {formatRelative(task.dueAt)}</span> : 'No date'}
        {task.completedAt && ` · Done ${formatDate(task.completedAt)}`}
      </span>
      {task.kind === 'ask_hr' && task.description && <p className={styles.question}>{task.description}</p>}
      {task.answer && <p className={styles.itemText}>Answer: {task.answer}</p>}
      <span className={styles.chips}>
        {task.isOverdue && !isDone && <ToneChip tone="urgent">Overdue</ToneChip>}
        {task.kind === 'ask_hr' && (
          <Chip tone="attention" plain>
            Question from {task.askedByName ?? 'a hiring manager'}
          </Chip>
        )}
        {task.kind === 'follow_up' && (
          <Chip tone="neutral" plain>
            Follow-up
          </Chip>
        )}
      </span>
    </div>
  );
}

function AddTaskForm({ onDone, onCancel }: { readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const [title, setTitle] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const save = async (): Promise<void> => {
    if (title.trim().length < 2) return;
    setIsSaving(true);
    try {
      await communicationApi.createTask({ title: title.trim(), dueOn: dueOn === '' ? null : dueOn });
      await queryClient.invalidateQueries({ queryKey: TASKS_KEY });
      show({ tone: 'success', message: 'Task added' });
      onDone();
    } catch (cause) {
      show({ tone: 'danger', message: "The task wasn't added", detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <form
      className={styles.compose}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <Field label="What needs doing" isRequired>
        {({ inputId }) => <Input id={inputId} value={title} maxLength={200} autoFocus onChange={(event) => setTitle(event.target.value)} />}
      </Field>
      <Field label="Due" isOptional>
        {({ inputId }) => <DatePicker id={inputId} label="Due" value={dueOn} onChange={setDueOn} timeZone="Asia/Dhaka" />}
      </Field>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" isPending={isSaving} disabled={title.trim().length < 2}>
          Add task
        </Button>
      </div>
    </form>
  );
}

function AnswerForm({ task, onAnswer, onCancel }: { readonly task: TaskRow; readonly onAnswer: (answer: string) => void; readonly onCancel: () => void }): ReactNode {
  const [answer, setAnswer] = useState('');
  return (
    <form
      className={styles.compose}
      onSubmit={(event) => {
        event.preventDefault();
        if (answer.trim().length >= 2) onAnswer(answer.trim());
      }}
    >
      {task.description && <p className={styles.question}>{task.description}</p>}
      <Field label="Your answer" isRequired hint={`${task.askedByName ?? 'The hiring manager'} receives it.`}>
        {({ inputId, describedBy }) => <Textarea id={inputId} aria-describedby={describedBy} value={answer} rows={4} maxLength={2000} autoFocus onChange={(event) => setAnswer(event.target.value)} />}
      </Field>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" disabled={answer.trim().length < 2}>
          Send answer
        </Button>
      </div>
    </form>
  );
}

function ReassignForm({ task, onDone, onCancel }: { readonly task: TaskRow; readonly onDone: () => void; readonly onCancel: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const people = useTaskAssignees(true);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const save = async (): Promise<void> => {
    if (!assignee) return;
    setIsSaving(true);
    try {
      const result = await communicationApi.reassignTask(task.id, task.version, assignee);
      await queryClient.invalidateQueries({ queryKey: TASKS_KEY });
      show({ tone: 'success', message: `Handed to ${result.assignee?.fullName ?? 'a colleague'}` });
      onDone();
    } catch (cause) {
      show({ tone: 'danger', message: "It wasn't handed over", detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div className={styles.compose}>
      <Field label="Hand to">
        {({ inputId }) => (
          <Select id={inputId} label="Hand to" value={assignee} placeholder="Choose a colleague" options={(people.data ?? []).filter((person) => person.id !== task.assignee?.id).map((person) => ({ value: person.id, label: person.fullName }))} onChange={setAssignee} />
        )}
      </Field>
      <div className={styles.formActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" isPending={isSaving} disabled={!assignee} onClick={() => void save()}>
          Hand over
        </Button>
      </div>
    </div>
  );
}
